import React, { useEffect, useRef, useState } from "react";
import { login, verifyOtp, forgotPasswordApi } from "../../api/authApi";
import { useNavigate } from "react-router-dom";
import { OTPInput, SlotProps } from "input-otp";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../store";
import { fetchMe } from "../../store/slices/authSlice";

const OtpSlot = (props: SlotProps) => (
    <div
        className={`w-11 h-12 border-2 rounded-xl flex items-center justify-center text-lg font-semibold transition-all
            ${props.isActive ? "border-primary bg-primary-light" : "border-gray-200 bg-white"}
            ${props.char ? "text-gray-900" : "text-gray-300"}`}
    >
        {props.char ?? <span className="text-gray-300">·</span>}
        {props.hasFakeCaret && (
            <span className="animate-pulse text-primary-light0 ml-0.5">|</span>
        )}
    </div>
);


const Login: React.FC = () => {
    const { user, initialized } = useSelector((state: RootState) => state.auth);
    const dispatch = useDispatch<AppDispatch>();
    const [step, setStep] = useState<"credentials" | "otp" | "forgot" | "forgot_success">("credentials");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [otp, setOtp] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    const navigate = useNavigate();
    const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        if (!initialized) {
            dispatch(fetchMe());
        }
    }, [dispatch, initialized]);

    useEffect(() => {
        if (initialized && user) {
            navigate(user.role_id === 1 ? "/management" : "/dashboard");
        }
    }, [initialized, user, navigate]);

    useEffect(() => {
        if (resendCooldown <= 0) return;
        cooldownRef.current = setInterval(() => {
            setResendCooldown((c) => {
                if (c <= 1) { clearInterval(cooldownRef.current!); return 0; }
                return c - 1;
            });
        }, 1000);
        return () => clearInterval(cooldownRef.current!);
    }, [resendCooldown]);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const res = await login({ email, password });
            if (res.data.step === "otp_required") {
                setStep("otp");
                setResendCooldown(60);
            }
        } catch (err: any) {
            setError(err?.response?.data?.error || "Invalid email or password");
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async () => {
        if (otp.length !== 6) return;
        setError("");
        setLoading(true);
        try {
            const res = await verifyOtp({ email, code: otp });
            const roleId = res.data.user?.role_id;
            setTimeout(() => {
                window.location.href = roleId === 1 ? "/management" : "/dashboard";
            }, 100);
        } catch (err: any) {
            setError(err?.response?.data?.error || "Invalid or expired OTP");
            setOtp("");
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (resendCooldown > 0) return;
        setError("");
        setOtp("");
        setLoading(true);
        try {
            await login({ email, password });
            setResendCooldown(60);
        } catch {
            setError("Failed to resend OTP");
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await forgotPasswordApi({ email });
            setStep("forgot_success");
        } catch (err: any) {
            setError(err?.response?.data?.error || "Failed to request password reset link");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex h-screen">
            <div className="hidden md:flex md:w-2/3 bg-gradient-to-br from-blue-50/80 via-indigo-50/40 to-slate-50/30 items-center justify-center p-12 relative border-r border-slate-100">
                <div className="absolute top-20 left-20 w-72 h-72 rounded-full bg-blue-100/20 blur-3xl" />
                <div className="absolute bottom-20 right-20 w-72 h-72 rounded-full bg-indigo-100/20 blur-3xl" />
                
                <div className="max-w-lg text-center flex flex-col items-center gap-8 z-10">
                    <img 
                        src="/login-illustration.svg" 
                        className="w-96 h-96 object-contain" 
                        alt="Login Illustration" 
                    />
                    <div className="space-y-3">
                        <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight">
                            Access Your Dashboard
                        </h1>
                        <p className="text-slate-500 text-sm max-w-md leading-relaxed font-medium">
                            Secure workspace with automated OTP verification to protect your leave requests, allowances, and team calendars.
                        </p>
                    </div>
                </div>
            </div>

            <div className="w-full md:w-1/3 flex flex-col justify-between px-12 py-10 bg-white">
                <div className="flex flex-col justify-center flex-1">

                    {step === "credentials" && (
                        <>
                            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Login to DayOff</h2>
                            <p className="text-sm text-gray-400 mb-8">Enter your credentials to continue</p>

                            <form onSubmit={handleLogin} className="flex flex-col gap-4">
                                <div>
                                    <label className="text-sm text-gray-600 mb-1 block">Email</label>
                                    <input
                                        type="email"
                                        placeholder="you@company.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-primary"
                                        required
                                    />
                                </div>
                                <div>
                                    <div className="flex justify-between items-center mb-1">
                                        <label className="text-sm text-gray-600 block">Password</label>
                                        <button
                                            type="button"
                                            onClick={() => { setStep("forgot"); setError(""); }}
                                            className="text-xs text-primary hover:underline font-medium focus:outline-none"
                                        >
                                            Forgot password?
                                        </button>
                                    </div>
                                    <input
                                        type="password"
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-primary"
                                        required
                                    />
                                </div>

                                {error && <p className="text-red-500 text-sm">{error}</p>}

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full py-3 rounded-xl text-white text-sm font-bold mt-2 disabled:opacity-60 bg-primary hover:bg-primary-dark shadow-md shadow-blue-50 transition-all"
                                >
                                    {loading ? "Checking…" : "Continue"}
                                </button>
                            </form>
                        </>
                    )}

                    {step === "otp" && (
                        <>
                            <button
                                onClick={() => { setStep("credentials"); setError(""); setOtp(""); }}
                                className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 mb-8 w-fit transition-colors"
                            >
                                ← Back
                            </button>

                            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Check your email</h2>
                            <p className="text-sm text-gray-400 mb-1">
                                We sent a 6-digit code to
                            </p>
                            <p className="text-sm font-medium text-gray-700 mb-8">{email}</p>

                            <OTPInput
                                maxLength={6}
                                value={otp}
                                onChange={setOtp}
                                render={({ slots }) => (
                                    <div className="flex gap-2 mb-6">
                                        {slots.map((slot, i) => <OtpSlot key={i} {...slot} />)}
                                    </div>
                                )}
                            />

                            {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

                            <button
                                onClick={handleVerifyOtp}
                                disabled={loading || otp.length !== 6}
                                className="w-full py-3 rounded-xl text-white text-sm font-bold disabled:opacity-50 bg-primary hover:bg-primary-dark shadow-md shadow-blue-50 transition-all"
                            >
                                {loading ? "Verifying…" : "Verify & Login"}
                            </button>

                            <div className="mt-4 text-center">
                                {resendCooldown > 0 ? (
                                    <p className="text-sm text-gray-400">Resend in {resendCooldown}s</p>
                                ) : (
                                    <button
                                        onClick={handleResend}
                                        className="text-sm text-primary hover:underline"
                                    >
                                        Resend code
                                    </button>
                                )}
                            </div>
                        </>
                    )}

                    {step === "forgot" && (
                        <>
                            <button
                                onClick={() => { setStep("credentials"); setError(""); }}
                                className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 mb-8 w-fit transition-colors"
                            >
                                ← Back to Login
                            </button>

                            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Reset Password</h2>
                            <p className="text-sm text-gray-400 mb-8">
                                Enter your email below to receive a secure link to choose a new password.
                            </p>

                            <form onSubmit={handleForgotPassword} className="flex flex-col gap-5">
                                <div>
                                    <label className="text-sm text-gray-600 mb-1 block">Email Address</label>
                                    <input
                                        type="email"
                                        placeholder="you@company.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-primary"
                                        required
                                    />
                                </div>

                                {error && <p className="text-red-500 text-sm">{error}</p>}

                                <button
                                    type="submit"
                                    disabled={loading || !email}
                                    className="w-full py-3 rounded-xl text-white text-sm font-bold mt-2 disabled:opacity-50 bg-primary hover:bg-primary-dark shadow-md shadow-blue-50 transition-all"
                                >
                                    {loading ? "Sending link…" : "Send Reset Link"}
                                </button>
                            </form>
                        </>
                    )}

                    {step === "forgot_success" && (
                        <div className="flex flex-col items-center text-center">
                            <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center text-green-500 mb-6 border border-green-100">
                                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Check your email</h2>
                            <p className="text-sm text-gray-400 max-w-sm mb-8 leading-relaxed">
                                If an account exists for <span className="font-semibold text-gray-600">{email}</span>, we have sent a secure password reset link to your inbox.
                            </p>
                            <button
                                onClick={() => { setStep("credentials"); setError(""); }}
                                className="w-full py-3 rounded-xl text-white text-sm font-bold bg-primary hover:bg-primary-dark shadow-md transition-all"
                            >
                                Return to Login
                            </button>
                        </div>
                    )}
                </div>

                <div className="text-xs text-gray-400 text-center">
                    By logging in, you agree to our{" "}
                    <span className="underline cursor-pointer">Terms of Use</span> and{" "}
                    <span className="underline cursor-pointer">Privacy Policy</span>
                </div>
            </div>
        </div>
    );
};

export default Login;
